import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import InputField from "components/Input/InputField";
import { useState } from "react";
import { toast } from "react-toastify";
import { getPartnerBankAccount, updatePartner } from "../referralPartnersApi";
import { PARTNER_FIELD_MAX_LENGTH } from "const/referralPartnerFields";

const FIELDS = [
  { name: "displayName", label: "Display name" },
  { name: "tradingName", label: "Trading name" },
  { name: "phone", label: "Phone" },
  { name: "entityType", label: "Entity type" },
  { name: "abn", label: "ABN" },
  { name: "addressLine1", label: "Address line 1" },
  { name: "addressLine2", label: "Address line 2" },
  { name: "suburb", label: "Suburb" },
  { name: "state", label: "State" },
  { name: "postcode", label: "Postcode" },
  { name: "country", label: "Country" },
  { name: "bankAccountName", label: "Bank account name" },
  { name: "bsb", label: "BSB" },
  { name: "accountNumber", label: "Account number" },
];

const Row = ({ label, value }) => (
  <div className="flex gap-3 py-1.5 border-b border-app-border last:border-0">
    <div className="w-44 shrink-0 text-sm text-app-text-muted">{label}</div>
    <div className="text-sm font-medium break-words">{value || "—"}</div>
  </div>
);

const PartnerProfilePanel = ({ partner, canUpdate, canRevealBank, onChanged }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [accountNumber, setAccountNumber] = useState(null);

  const openEdit = () => {
    const next = {};
    [...FIELDS.map((field) => field.name), "commissionNotes"].forEach((name) => {
      next[name] = partner[name] ?? "";
    });
    next.allowIndependentDocuments = partner.allowIndependentDocuments !== false;
    // Masked on every read, so the box starts empty and an empty box keeps what is stored.
    next.accountNumber = "";
    setForm(next);
    setEditOpen(true);
  };

  const saveMutation = useMutation((payload) => updatePartner(partner.id, payload), {
    onSuccess: () => {
      toast.success("Partner updated");
      setEditOpen(false);
      onChanged?.();
    },
    onError: (err) =>
      toast.error(
        err?.response?.data?.errorMessage || err?.response?.data?.message || "Could not update"
      ),
  });

  const patch = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    const payload = { ...form };
    // An untouched account-number box must not wipe the stored number.
    if (!String(payload.accountNumber || "").trim()) delete payload.accountNumber;
    saveMutation.mutate(payload);
  };

  const revealMutation = useMutation(() => getPartnerBankAccount(partner.id), {
    onSuccess: (details) => setAccountNumber(details?.accountNumber || ""),
    onError: (err) =>
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Could not read the account number"
      ),
  });

  return (
    <div className="bg-app-surface rounded shadow p-4">
      <div className="flex items-center justify-between mb-2">
        <Typography variant="h6">Profile</Typography>
        {canUpdate ? (
          <Button size="small" variant="outlined" onClick={openEdit}>
            Edit
          </Button>
        ) : null}
      </div>
      <Row label="Email" value={partner.email} />
      <Row label="Display name" value={partner.displayName} />
      <Row label="Trading name" value={partner.tradingName} />
      <Row label="Phone" value={partner.phone} />
      <Row label="Entity type" value={partner.entityType} />
      <Row label="ABN" value={partner.abn} />
      <Row label="TFN (last 4)" value={partner.tfnLast4} />
      <Row label="GST registered" value={partner.gstRegistered ? "Yes" : "No"} />
      <Row
        label="Address"
        value={[
          partner.addressLine1,
          partner.addressLine2,
          partner.suburb,
          partner.state,
          partner.postcode,
          partner.country,
        ]
          .filter(Boolean)
          .join(", ")}
      />
      <Row label="Bank account name" value={partner.bankAccountName} />
      <Row label="BSB" value={partner.bsb} />
      <Row
        label="Account number"
        value={
          accountNumber ? (
            <span className="tabular-nums">{accountNumber}</span>
          ) : (
            <span className="flex flex-wrap items-center gap-2">
              <span>{partner.accountNumberLast4 ? `•••• ${partner.accountNumberLast4}` : "—"}</span>
              {canRevealBank && partner.accountNumberLast4 ? (
                <Button
                  size="small"
                  variant="text"
                  disabled={revealMutation.isLoading}
                  onClick={() => revealMutation.mutate()}
                >
                  {revealMutation.isLoading ? "Reading…" : "Reveal"}
                </Button>
              ) : null}
            </span>
          )
        }
      />
      <Row label="Commission notes" value={partner.commissionNotes} />
      <Row
        label="Independent documents"
        value={
          partner.allowIndependentDocuments !== false
            ? "Yes — can raise requests and upload without being asked"
            : "No — works only the requests the office sends"
        }
      />
      <Row label="Terms version" value={partner.termsVersion} />
      <Row
        label="Onboarded"
        value={partner.onboardedAt ? new Date(partner.onboardedAt).toLocaleString() : "—"}
      />

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit partner profile</DialogTitle>
        <DialogContent dividers>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            {FIELDS.map((field) => (
              <InputField
                key={field.name}
                label={field.label}
                name={field.name}
                value={form[field.name] ?? ""}
                onChange={patch}
                maxLength={PARTNER_FIELD_MAX_LENGTH[field.name]}
              />
            ))}
          </div>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Commission notes"
            name="commissionNotes"
            value={form.commissionNotes ?? ""}
            onChange={patch}
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={form.allowIndependentDocuments !== false}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    allowIndependentDocuments: event.target.checked,
                  }))
                }
              />
            }
            label="Let this partner raise document requests and upload files without being asked first"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saveMutation.isLoading} onClick={handleSave}>
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PartnerProfilePanel;
